<template>
  <MultiSelectComponent
    :modelValue="selectedCompanyIds"
    @update:modelValue="onInternalUpdate"
    :options="companyOptions"
    :isLoading="companiesLoading"
    :isError="companiesError"
    :placeholder="placeholder"
    :size="size"
  />
</template>

<script setup lang="ts">
import {
  fetchAdminCompanies,
  type AdminCompany,
} from "@/components/common/adminCompaniesApi";
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { computed, onMounted, ref } from "vue";

/**
 * Company selector built on top of MultiSelectComponent.
 *
 * Encapsulates fetching company options and normalizes v-model values to number[].
 */
const props = withDefaults(
  defineProps<{
    /** Placeholder shown when nothing selected */
    placeholder?: string;
    /** Visual size of the control */
    size?: "sm" | "md";
    /** Auto fetch companies on mount */
    autoFetch?: boolean;
  }>(),
  {
    size: "md",
    autoFetch: true,
  }
);

// Two-way binding for selected company ids using Vue 3.4+ defineModel macro
const selectedCompanyIds = defineModel<number[]>({ default: () => [] });

const emit = defineEmits<{
  error: [error: unknown];
}>();

interface MultiSelectOption {
  label: string;
  value: number;
  description?: string;
}

const companiesLoading = ref(false);
const companiesError = ref(false);
const availableCompanies = ref<AdminCompany[]>([]);
const { globalErrorHandler } = useGlobalHandlers();

const fetchCompanies = async (): Promise<void> => {
  try {
    companiesLoading.value = true;
    companiesError.value = false;
    availableCompanies.value = await fetchAdminCompanies();
  } catch (error) {
    globalErrorHandler?.(error);
    emit("error", error);
    companiesError.value = true;
  } finally {
    companiesLoading.value = false;
  }
};

onMounted(() => {
  if (props.autoFetch) fetchCompanies();
});

defineExpose({
  fetchCompanies,
});

const companyOptions = computed<MultiSelectOption[]>(() => {
  return availableCompanies.value.map((company) => ({
    label: company.deleted_at ? `${company.name} (deleted)` : company.name,
    value: company.id,
    description: `${company.country} - ${company.id}`,
  }));
});

// Pass-through model for base component (accepts (string|number)[])
const onInternalUpdate = (value: (string | number)[]) => {
  const normalized = Array.from(
    new Set(value.map((v) => Number(v)).filter((v) => isFinite(v)))
  );
  selectedCompanyIds.value = normalized;
};
</script>

<style scoped lang="scss"></style>
